import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogOut, Users, TrendingUp, CheckCircle, Clock, Plus, Edit, Trash2, Search, Download, FileSpreadsheet } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [formData, setFormData] = useState({
    name: '', dob: '', email: '', contact_number: '',
    college_name: '', course_name: '', intake: '', company_consultant: '',
    current_stage: 'Applied', is_converted: false, converted_by_staff: '',
    total_payment_received: 0, payment_screenshots: [], commission_received: false, notes: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || userData.role !== 'admin') {
      navigate('/login');
      return;
    }
    
    setUser(userData);
    fetchStudents();
    fetchStats();
  }, []);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/students`, getAuthHeader());
      setStudents(response.data);
    } catch (error) {
      toast.error('Failed to load students');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/students/stats/overview`, getAuthHeader());
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStudent) {
        await axios.put(`${API}/students/${editingStudent.student_id}`, formData, getAuthHeader());
        toast.success('Student updated successfully');
      } else {
        await axios.post(`${API}/students`, formData, getAuthHeader());
        toast.success('Student added successfully');
      }
      setShowForm(false);
      setEditingStudent(null);
      setFormData({
        name: '', dob: '', email: '', contact_number: '',
        college_name: '', course_name: '', intake: '', company_consultant: '',
        current_stage: 'Applied', is_converted: false, converted_by_staff: '',
        total_payment_received: 0, payment_screenshots: [], commission_received: false, notes: ''
      });
      fetchStudents();
      fetchStats();
    } catch (error) {
      toast.error('Failed to save student');
    }
  };

  const handleEdit = (student) => {
    setEditingStudent(student);
    setFormData({
      name: student.name,
      dob: student.dob,
      email: student.email,
      contact_number: student.mobile_number || student.contact_number || '',
      college_name: student.college_name,
      course_name: student.course_name,
      intake: student.intake || '',
      company_consultant: student.company_consultant,
      current_stage: student.current_stage,
      is_converted: student.is_converted,
      converted_by_staff: student.converted_by_staff || '',
      total_payment_received: student.total_payment_received || 0,
      payment_screenshots: student.payment_screenshots || [],
      commission_received: student.commission_received || false,
      notes: student.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (student_id) => {
    if (!window.confirm('Are you sure you want to delete this student?')) return;
    try {
      await axios.delete(`${API}/students/${student_id}`, getAuthHeader());
      toast.success('Student deleted');
      fetchStudents();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete student');
    }
  };

  const exportToExcel = () => {
    const exportData = students.map(s => ({
      'Student Name': s.name,
      'Date of Birth': s.dob,
      'Email': s.email,
      'Contact Number': s.mobile_number || s.contact_number,
      'College': s.college_name,
      'Course': s.course_name,
      'Intake': s.intake,
      'Company/Consultant': s.company_consultant,
      'Current Stage': s.current_stage,
      'Converted': s.is_converted ? 'Yes' : 'No',
      'Converted By': s.converted_by_staff || '-',
      'Notes': s.notes || '-',
      'Created At': new Date(s.created_at).toLocaleDateString()
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Students');
    
    // Auto-size columns
    const colWidths = Object.keys(exportData[0] || {}).map(key => ({
      wch: Math.max(key.length, ...exportData.map(row => String(row[key]).length))
    }));
    ws['!cols'] = colWidths;
    
    XLSX.writeFile(wb, `ELARA_Students_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel file downloaded successfully!');
  };

  const filteredStudents = students.filter(s => 
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.college_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stages = ['Applied', 'Documents Submitted', 'Interview Scheduled', 'Admitted', 'Enrolled', 'Rejected'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Premium Header */}
      <div className="bg-gradient-to-r from-primary via-slate-800 to-primary shadow-2xl border-b-4 border-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-6">
              <img
                src="https://customer-assets.emergentagent.com/job_canada-pathway-3/artifacts/wzgtavxp_IMG_0276-removebg-preview.png"
                alt="ELARA"
                className="h-14 w-auto"
              />
              <div className="border-l-2 border-secondary pl-6">
                <h1 className="text-3xl font-playfair font-bold text-white tracking-wide">Admin Dashboard</h1>
                <p className="text-sm text-secondary mt-1">Welcome back, <span className="font-semibold">{user?.full_name}</span></p>
              </div>
            </div>
            <button 
              onClick={handleLogout} 
              className="flex items-center gap-3 px-6 py-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 transition-all duration-300 group"
            >
              <LogOut size={18} className="group-hover:rotate-180 transition-transform duration-500" /> 
              <span className="text-white font-semibold">Logout</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Premium Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-10">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-white to-slate-50 p-6 shadow-xl border-l-4 border-primary hover:shadow-2xl transition-all duration-300 group"
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Total Students</p>
                  <p className="text-4xl font-playfair font-bold text-primary">{stats.total_students}</p>
                </div>
                <div className="p-3 bg-primary/10 rounded-full group-hover:scale-110 transition-transform">
                  <Users className="text-primary" size={24} />
                </div>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-white to-green-50 p-6 shadow-xl border-l-4 border-green-600 hover:shadow-2xl transition-all duration-300 group"
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Converted</p>
                  <p className="text-4xl font-playfair font-bold text-green-600">{stats.converted_students}</p>
                </div>
                <div className="p-3 bg-green-600/10 rounded-full group-hover:scale-110 transition-transform">
                  <CheckCircle className="text-green-600" size={24} />
                </div>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-white to-amber-50 p-6 shadow-xl border-l-4 border-secondary hover:shadow-2xl transition-all duration-300 group"
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Success Rate</p>
                  <p className="text-4xl font-playfair font-bold text-secondary">{stats.conversion_rate}%</p>
                </div>
                <div className="p-3 bg-secondary/10 rounded-full group-hover:scale-110 transition-transform">
                  <TrendingUp className="text-secondary" size={24} />
                </div>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-br from-white to-blue-50 p-6 shadow-xl border-l-4 border-blue-600 hover:shadow-2xl transition-all duration-300 group"
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Total Payment</p>
                  <p className="text-4xl font-playfair font-bold text-blue-600">${stats.total_payment_received || 0}</p>
                </div>
                <div className="p-3 bg-blue-600/10 rounded-full group-hover:scale-110 transition-transform">
                  <Clock className="text-blue-600" size={24} />
                </div>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-br from-white to-purple-50 p-6 shadow-xl border-l-4 border-purple-600 hover:shadow-2xl transition-all duration-300 group"
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Commission</p>
                  <p className="text-4xl font-playfair font-bold text-purple-600">{stats.commission_received_count || 0}</p>
                </div>
                <div className="p-3 bg-purple-600/10 rounded-full group-hover:scale-110 transition-transform">
                  <CheckCircle className="text-purple-600" size={24} />
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Action Bar */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 bg-white p-6 shadow-lg border-l-4 border-secondary">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search students by name, email, or college..."
                className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 focus:outline-none focus:border-secondary transition-colors font-medium"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={exportToExcel}
              disabled={students.length === 0}
              className="px-6 py-3 bg-green-600 text-white font-semibold hover:bg-green-700 transition-all duration-300 shadow-md hover:shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={20} />
              Export Excel
            </button>
            <button
              onClick={() => { setShowForm(true); setEditingStudent(null); }}
              className="px-6 py-3 bg-gradient-to-r from-secondary to-secondary-light text-primary font-bold hover:from-secondary-light hover:to-secondary transition-all duration-300 shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <Plus size={20} /> Add Student
            </button>
          </div>
        </div>

        {/* Student Form */}
        {showForm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white w-full max-w-4xl my-8 shadow-2xl border border-slate-200"
            >
              {/* Form Header */}
              <div className="bg-gradient-to-r from-primary via-primary-light to-primary px-8 py-6 border-b-2 border-secondary">
                <h2 className="text-3xl font-playfair font-bold text-white">
                  {editingStudent ? 'Edit Student Details' : 'Add New Student'}
                </h2>
                <p className="text-slate-200 mt-1">Fill in the student information below</p>
              </div>

              {/* Form Content */}
              <form onSubmit={handleSubmit} className="p-8">
                {/* Personal Information Section */}
                <div className="mb-8">
                  <h3 className="text-xl font-playfair font-semibold text-primary mb-4 pb-2 border-b-2 border-secondary/30">
                    Personal Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Student Name *</label>
                      <input 
                        type="text" 
                        placeholder="Full Name" 
                        value={formData.name} 
                        onChange={(e) => setFormData({...formData, name: e.target.value})} 
                        required 
                        className="w-full px-4 py-3 border-2 border-slate-300 focus:outline-none focus:border-secondary transition-colors font-medium"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Date of Birth *</label>
                      <input 
                        type="date" 
                        value={formData.dob} 
                        onChange={(e) => setFormData({...formData, dob: e.target.value})} 
                        required 
                        className="w-full px-4 py-3 border-2 border-slate-300 focus:outline-none focus:border-secondary transition-colors font-medium"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Email Address *</label>
                      <input 
                        type="email" 
                        placeholder="student@example.com" 
                        value={formData.email} 
                        onChange={(e) => setFormData({...formData, email: e.target.value})} 
                        required 
                        className="w-full px-4 py-3 border-2 border-slate-300 focus:outline-none focus:border-secondary transition-colors font-medium"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Contact Number *</label>
                      <input 
                        type="tel" 
                        placeholder="+1 (XXX) XXX-XXXX" 
                        value={formData.contact_number} 
                        onChange={(e) => setFormData({...formData, contact_number: e.target.value})} 
                        required 
                        className="w-full px-4 py-3 border-2 border-slate-300 focus:outline-none focus:border-secondary transition-colors font-medium"
                      />
                    </div>
                  </div>
                </div>

                {/* Academic Information Section */}
                <div className="mb-8">
                  <h3 className="text-xl font-playfair font-semibold text-primary mb-4 pb-2 border-b-2 border-secondary/30">
                    Academic Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">College Name *</label>
                      <input 
                        type="text" 
                        placeholder="College Name" 
                        value={formData.college_name} 
                        onChange={(e) => setFormData({...formData, college_name: e.target.value})} 
                        required 
                        className="w-full px-4 py-3 border-2 border-slate-300 focus:outline-none focus:border-secondary transition-colors font-medium"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Course Name *</label>
                      <input 
                        type="text" 
                        placeholder="Course/Program Name" 
                        value={formData.course_name} 
                        onChange={(e) => setFormData({...formData, course_name: e.target.value})} 
                        required 
                        className="w-full px-4 py-3 border-2 border-slate-300 focus:outline-none focus:border-secondary transition-colors font-medium"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Intake *</label>
                      <select 
                        value={formData.intake} 
                        onChange={(e) => setFormData({...formData, intake: e.target.value})} 
                        required 
                        className="w-full px-4 py-3 border-2 border-slate-300 focus:outline-none focus:border-secondary transition-colors font-medium"
                      >
                        <option value="">Select Intake</option>
                        <option value="Spring 2025">Spring 2025</option>
                        <option value="Fall 2025">Fall 2025</option>
                        <option value="Winter 2026">Winter 2026</option>
                        <option value="Spring 2026">Spring 2026</option>
                        <option value="Fall 2026">Fall 2026</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Company/Consultant *</label>
                      <input 
                        type="text" 
                        placeholder="Consultant Name" 
                        value={formData.company_consultant} 
                        onChange={(e) => setFormData({...formData, company_consultant: e.target.value})} 
                        required 
                        className="w-full px-4 py-3 border-2 border-slate-300 focus:outline-none focus:border-secondary transition-colors font-medium"
                      />
                    </div>
                  </div>
                </div>

                {/* Application Status Section */}
                <div className="mb-8">
                  <h3 className="text-xl font-playfair font-semibold text-primary mb-4 pb-2 border-b-2 border-secondary/30">
                    Application Status
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Current Stage *</label>
                      <select 
                        value={formData.current_stage} 
                        onChange={(e) => setFormData({...formData, current_stage: e.target.value})} 
                        className="w-full px-4 py-3 border-2 border-slate-300 focus:outline-none focus:border-secondary transition-colors font-medium"
                      >
                        {stages.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Converted By Staff</label>
                      <input 
                        type="text" 
                        placeholder="Staff Name" 
                        value={formData.converted_by_staff} 
                        onChange={(e) => setFormData({...formData, converted_by_staff: e.target.value})} 
                        className="w-full px-4 py-3 border-2 border-slate-300 focus:outline-none focus:border-secondary transition-colors font-medium"
                      />
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-3 px-4 py-3 border-2 border-slate-300 cursor-pointer hover:border-secondary transition-colors">
                        <input 
                          type="checkbox" 
                          checked={formData.is_converted} 
                          onChange={(e) => setFormData({...formData, is_converted: e.target.checked})} 
                          className="w-5 h-5 accent-secondary"
                        />
                        <span className="font-semibold text-slate-700">Student Converted</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Notes Section */}
                <div className="mb-8">
                  <h3 className="text-xl font-playfair font-semibold text-primary mb-4 pb-2 border-b-2 border-secondary/30">
                    Additional Notes
                  </h3>
                  <textarea 
                    placeholder="Add any additional notes or comments..." 
                    value={formData.notes} 
                    onChange={(e) => setFormData({...formData, notes: e.target.value})} 
                    rows={4} 
                    className="w-full px-4 py-3 border-2 border-slate-300 focus:outline-none focus:border-secondary transition-colors font-medium resize-none"
                  />
                </div>

                {/* Form Actions */}
                <div className="flex gap-4 pt-6 border-t-2 border-slate-200">
                  <button 
                    type="submit" 
                    className="flex-1 px-8 py-4 bg-gradient-to-r from-secondary to-secondary-light text-primary font-bold text-lg hover:from-secondary-light hover:to-secondary transition-all duration-300 shadow-lg"
                  >
                    {editingStudent ? 'Update Student' : 'Save Student'}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => { setShowForm(false); setEditingStudent(null); }} 
                    className="px-8 py-4 border-2 border-slate-400 text-slate-700 font-semibold hover:bg-slate-100 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* Premium Students Table */}
        <div className="bg-white shadow-2xl overflow-hidden border-t-4 border-secondary">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-primary to-slate-800">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Student Info</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">College & Course</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Intake</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Stage</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Converted By</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredStudents.map((student, index) => (
                  <tr key={student.student_id} className="hover:bg-slate-50 transition-colors duration-150">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-semibold text-slate-900">{student.name}</div>
                        <div className="text-sm text-slate-500">{student.email}</div>
                        <div className="text-xs text-slate-400 mt-1">{student.contact_number || student.mobile_number}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-slate-900">{student.college_name}</div>
                        <div className="text-sm text-slate-600">{student.course_name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold">{student.intake || '-'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 bg-purple-100 text-purple-800 text-xs font-semibold">{student.current_stage}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700 font-medium">{student.converted_by_staff || '-'}</td>
                    <td className="px-6 py-4">
                      {student.is_converted ? (
                        <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-bold uppercase">Converted</span>
                      ) : (
                        <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-bold uppercase">In Progress</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center gap-2">
                        <button 
                          onClick={() => handleEdit(student)} 
                          className="p-2 bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors group"
                          title="Edit"
                        >
                          <Edit size={18} className="group-hover:scale-110 transition-transform" />
                        </button>
                        <button 
                          onClick={() => handleDelete(student.student_id)} 
                          className="p-2 bg-red-100 text-red-600 hover:bg-red-200 transition-colors group"
                          title="Delete"
                        >
                          <Trash2 size={18} className="group-hover:scale-110 transition-transform" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredStudents.length === 0 && (
              <div className="text-center py-16">
                <Users className="mx-auto text-slate-300 mb-4" size={64} />
                <p className="text-xl font-semibold text-slate-400">No students found</p>
                <p className="text-sm text-slate-400 mt-2">Add your first student to get started</p>
              </div>
            )}
          </div>
          
          {/* Table Footer */}
          {filteredStudents.length > 0 && (
            <div className="bg-slate-50 px-6 py-4 border-t border-slate-200">
              <p className="text-sm text-slate-600">
                Showing <span className="font-semibold text-slate-900">{filteredStudents.length}</span> of{' '}
                <span className="font-semibold text-slate-900">{students.length}</span> students
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};